import {
  Field,
  FieldError,
  FieldInput,
  FieldLabel,
} from "@strapi/design-system";
import { Grid, GridItem } from "@strapi/design-system/Grid";
import { Stack } from "@strapi/design-system/Stack";
import { Button, DatePicker, Radio, Select, message, Modal, Card } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import countryList from "react-select-country-list";
import { createNominee, getNominee, updateNominee } from "../util/API";
import {
  dateFormatList,
  isStrEmpty,
  isValidEmail,
  isValidPhoneNumber,
} from "../util/helper";
import DegreeSelect from "./degreeSelect";
import { red } from "@ant-design/colors";
import { LoadingOutlined } from "@ant-design/icons";

const Nominee = ({ data, mode, handleOnClose = () => {} }) => {
  const { t } = useTranslation();
  const countries = useMemo(() => countryList().getData(), []);
  const userInfo = useSelector((state) => state.app.userInfo);
  const [dataForUpdate, setDataForUpdate] = useState();
  const [messageApi, contextHolder] = message.useMessage();

  const [changeEmailConfirmed, setChangeEmailConfirmed] = useState(false);
  const [showConfirmChangeEmailModal, setShowConfirmChangeEmailModal] =
    useState(false);

  const [showLoadingButton, setShowLoadingButton] = useState(false);

  useEffect(() => {
    const getNomineeInfo = async (api_path) => {
      try {
        const response = await getNominee(api_path);
        setDataForUpdate(response.data);
      } catch (error) {
        console.error(error);
        // TODO: Handle error while fetching profile
      }
    };

    if (mode === "UPDATE" && !dataForUpdate?.id) {
      const api_path = data?.api_path ?? "/api/nominatees/" + data?.id;

      getNomineeInfo(api_path);
    }
  }, [data?.api_path, data?.id, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowLoadingButton(true);

    const temp = { ...(dataForUpdate || {}) };
    if (!temp?.dob || temp?.dob === null || temp?.dob === "") {
      temp.dob = dayjs().format("YYYY-MM-DD");
    }

    const checkValid = isValid();
    if (!checkValid) {
      messageApi.open({
        type: "error",
        content: t("str_fill_in_all_info"),
      });

      setShowLoadingButton(false);
      return;
    }

    const checkEmailValid = isValidEmail(temp?.email);
    if (!checkEmailValid) {
      messageApi.open({
        type: "error",
        content: t("str_email_invalid"),
      });

      setShowLoadingButton(false);
      return;
    }

    if (data?.id) {
      //* Check if email changed
      if (!changeEmailConfirmed && isEmailChanged()) {
        setShowConfirmChangeEmailModal(true);
        return;
      }

      try {
        const response = await updateNominee(
          "/api/nominatees/" + data.id,
          temp
        );

        if (response.status === 200) {
          messageApi.open({
            type: "success",
            content: `${t("str_save_success")}`,
          });
        }

        setShowLoadingButton(false);
        return;
      } catch (error) {}
    } else {
      if (!isEmailValid()) {
        messageApi.open({
          type: "error",
          content: "Email không hợp lệ. Vui lòng kiểm tra lại.",
        });

        setShowLoadingButton(false);
        return;
      }

      try {
        const response = await createNominee(temp);
        if (response.status === 200) {
          messageApi.open({
            type: "success",
            content: `${t("str_save_success")}`,
          });

          setShowLoadingButton(false);
          handleOnClose();
        }
      } catch (error) {
        setShowLoadingButton(false);
      }
    }
  };

  const isValid = () => {
    //* MAIN INFO
    if (isStrEmpty(dataForUpdate?.name)) return false;
    if (isStrEmpty(dataForUpdate?.email)) return false;

    //* OPTIONAL TEXT FIELDS INFO
    if (isStrEmpty(dataForUpdate?.gender)) return false;
    return true;
  };

  const isEmailValid = () => {
    if (dataForUpdate?.email?.trim() === userInfo?.email) return false;

    return true;
  };

  //#region Handle update info
  const handleSetDob = (date, dateString) => {
    setDataForUpdate({
      ...dataForUpdate,
      dob: dayjs(date).format(dateFormatList[1]),
    });
  };

  const handleSetName = (name) => {
    setDataForUpdate({
      ...dataForUpdate,
      name,
    });
  };

  const handleSetAddress = (address) => {
    setDataForUpdate({
      ...dataForUpdate,
      address,
    });
  };

  const handleSetPhone = (phone) => {
    const formattedPhoneNumber = phone.replace(/[^0-9+\-().\s]/g, "");

    setDataForUpdate({
      ...dataForUpdate,
      phone: formattedPhoneNumber,
    });
  };

  const handleSetEmail = (email) => {
    setDataForUpdate({
      ...dataForUpdate,
      email,
    });
  };

  const handleSetGender = (gender) => {
    setDataForUpdate({
      ...dataForUpdate,
      gender,
    });
  };
  const handleSetThesisName = (name) => {
    setDataForUpdate({
      ...dataForUpdate,
      thesis: { ...dataForUpdate.thesis, name },
    });
  };
  const handleSetSpecialization = (specialization) => {
    setDataForUpdate({
      ...dataForUpdate,
      thesis: { ...dataForUpdate.thesis, specialization },
    });
  };
  const handleSetSchool = (school) => {
    setDataForUpdate({
      ...dataForUpdate,
      school,
    });
  };
  //#endregion
  const isEmailChanged = () => {
    if (dataForUpdate?.email !== data?.attributes?.email) return true;
    return false;
  };

  const handleConfirmedChangeEmail = async () => {
    setChangeEmailConfirmed(true);

    try {
      const res = await updateNominee(
        "/api/nominatees/" + data.id,
        dataForUpdate
      );

      if (res.status === 200) {
        setDataForUpdate(res.data);
        messageApi.open({
          type: "success",
          content: `${t("str_save_success")}`,
        });
        setShowConfirmChangeEmailModal(false);
        return;
      }
    } catch (error) {}
  };

  const handleCancelChangeEmail = () => {
    setDataForUpdate({ ...dataForUpdate, email: data?.attributes?.email });
    setShowConfirmChangeEmailModal(false);
  };

  return (
    <div>
      {contextHolder}
      <div>
        <Grid gridCols={4} gap={5}>
          <GridItem col={1} s={12} xs={12}>
            <Field
              name="name"
              error={
                !dataForUpdate?.name || dataForUpdate?.name === ""
                  ? t("str_required_full_name")
                  : ""
              }
            >
              <FieldLabel required style={{ marginBottom: "5px" }}>
                {t("str_full_name_nominee")}
              </FieldLabel>
              <FieldInput
                value={dataForUpdate?.name}
                onChange={(e) => handleSetName(e.target.value)}
              />
              <FieldError />
            </Field>
          </GridItem>

          <GridItem col={1} s={12} xs={12}>
            <Field name="dob">
              <FieldLabel style={{ marginBottom: "5px" }}>
                {t("str_dob")}
              </FieldLabel>
              <DatePicker
                value={dataForUpdate?.dob ? dayjs(dataForUpdate?.dob) : null}
                format={dateFormatList[0]}
                placeholder="dd/mm/yyyy"
                className="custom-date-picker"
                onChange={handleSetDob}
              />
            </Field>
          </GridItem>

          <GridItem col={1} s={12} xs={12}>
            <Field
              name="gender"
              error={
                !dataForUpdate?.gender || dataForUpdate?.gender === ""
                  ? t("str_required_gender")
                  : ""
              }
            >
              <FieldLabel required style={{ marginBottom: "5px" }}>
                {t("str_gender")}
              </FieldLabel>
              <Radio.Group
                value={dataForUpdate?.gender}
                onChange={(e) => handleSetGender(e.target.value)}
                style={{ marginTop: "10px" }}
              >
                <Radio value="male">{t("str_male")}</Radio>
                <Radio value="female">{t("str_female")}</Radio>
              </Radio.Group>

              <FieldError />
            </Field>
          </GridItem>

          <GridItem col={1} s={12} xs={12}>
            <Field name="address">
              <FieldLabel style={{ marginBottom: "5px" }}>
                {t("str_nominator_address")}
              </FieldLabel>
              <FieldInput
                value={dataForUpdate?.address}
                onChange={(e) => handleSetAddress(e.target.value)}
              />
            </Field>
          </GridItem>

          <GridItem col={1} s={12} xs={12}>
            <Field name="phone">
              <FieldLabel style={{ marginBottom: "5px" }}>
                {t("str_nominator_phone_number")}
              </FieldLabel>
              <FieldInput
                type="tel"
                value={dataForUpdate?.phone}
                onChange={(e) => handleSetPhone(e.target.value)}
                pattern="^\+?[0-9]{1,4}[-\s.]?(\(?[0-9]+\)?[-\s.]?){4,}$" // Set the pattern attribute for HTML5 validation
              />
              <FieldError />
            </Field>
          </GridItem>
          <GridItem col={1} s={12} xs={12}>
            <Field
              name="email"
              error={
                !dataForUpdate?.email ||
                dataForUpdate?.email === "" ||
                dataForUpdate?.email === null
                  ? t("str_required_email")
                  : ""
              }
            >
              <FieldLabel required style={{ marginBottom: "5px" }}>
                {t("str_nominator_email")}
              </FieldLabel>
              <FieldInput
                value={dataForUpdate?.email}
                onChange={(e) => handleSetEmail(e.target.value)}
              />
              <FieldError />
            </Field>
          </GridItem>
        </Grid>
      </div>
      <div style={{ marginTop: "20px" }}>
        <Card title={<span>{t("str_nominated_thesis")}</span>}>
          <Grid gridCols={3} gap={5}>
            <GridItem col={1} s={12} xs={12}>
              <Field name="thesis_name">
                <FieldLabel style={{ marginBottom: "5px" }}>
                  {t("str_thesis_name")}
                </FieldLabel>
                <FieldInput
                  value={dataForUpdate?.thesis?.name}
                  onChange={(e) => handleSetThesisName(e.target.value)}
                />
                <FieldError />
              </Field>
            </GridItem>
            <GridItem col={1} s={12} xs={12}>
              <Field name="specialization">
                <FieldLabel style={{ marginBottom: "5px" }}>
                  {t("str_thesis_majors")}
                </FieldLabel>
                <FieldInput
                  value={dataForUpdate?.thesis?.specialization}
                  onChange={(e) => handleSetSpecialization(e.target.value)}
                />
                <FieldError />
              </Field>
            </GridItem>
            <GridItem col={1} s={12} xs={12}>
              <Field name="school">
                <FieldLabel style={{ marginBottom: "5px" }}>
                  {t("str_graduation_school")}
                </FieldLabel>
                <FieldInput
                  value={dataForUpdate?.school}
                  onChange={(e) => handleSetSchool(e.target.value)}
                />
                <FieldError />
              </Field>
            </GridItem>
          </Grid>
        </Card>
      </div>
      <br />
      <Stack spacing={4} horizontal>
        <Button
          className="btnSave"
          onClick={handleSubmit}
          disabled={showLoadingButton}
        >
          {showLoadingButton && <LoadingOutlined />}
          {t("str_save")}
        </Button>
        {mode === "CREATE" && (
          <Button onClick={handleOnClose}>{t("str_cancel")}</Button>
        )}
      </Stack>

      <Modal
        title={t("str_email_change_confirm")}
        centered
        open={showConfirmChangeEmailModal}
        onOk={handleConfirmedChangeEmail}
        onCancel={handleCancelChangeEmail}
        okText={t("str_confirm")}
        cancelText={t("str_cancel")}
      >
        <p>
          {t("str_email_change_confirm_str1")}{" "}
          <span style={{ color: red[7], fontWeight: "500" }}>
            {userInfo?.email}
          </span>{" "}
          {t("str_email_change_confirm_str2")}{" "}
          <span style={{ color: red[7], fontWeight: "500" }}>
            {dataForUpdate?.email}
          </span>
        </p>
      </Modal>
    </div>
  );
};

export default Nominee;
